/**
 * Better Auth Type Extensions
 *
 * Extends Better Auth types to bridge the gap between minimal session data (ADR-004)
 * and full User model from database.
 *
 * @see ADR-004: Simplified Session Data Model
 * @see ADR-008: TypeScript Type Safety Patterns
 */

import type {
  User as BetterAuthUser,
  Session as BetterAuthSession,
} from "./better-auth";

/**
 * Better Auth Session User (fields cached in session)
 * - Better Auth session includes: id, email, name, image, createdAt, updatedAt, emailVerified
 * - Admin plugin adds: role, banned, banReason, banExpires (per ADR-004)
 * - firstName, lastName are NOT in session - only available from database queries
 * - lastLoginMethod is added by lastLoginMethod plugin
 *
 * NOTE: This type represents ONLY session data, NOT database relations
 * For database relations, use ExtendedUser type
 */
export type SessionUser = BetterAuthUser & {
  role?: string | null; // Added by admin plugin
  banned?: boolean | null; // Added by admin plugin
  banReason?: string | null; // Added by admin plugin
  banExpires?: Date | null; // Added by admin plugin
  lastLoginMethod?: string | null; // Added by lastLoginMethod plugin
};

/**
 * Extended User type for database queries
 * Includes fields from User model that aren't in session
 * Uses Prisma validator pattern for type safety
 *
 * Note: firstName/lastName are only available via database queries, not in session
 */
export type ExtendedUser = SessionUser & {
  firstName?: string | null;
  lastName?: string | null;
  propertySeeker?: {
    id: string;
    createdAt: Date;
    updatedAt: Date;
    createdById: string;
    updatedById: string | null;
    deletedAt: Date | null;
    deletedById: string | null;
    biography: any | null; // JSON field
    creditScoreRange:
      | "NOT_PROVIDED"
      | "NOT_REQUIRED"
      | "POOR"
      | "FAIR"
      | "GOOD"
      | "VERY_GOOD"
      | "EXCELLENT";
    annualIncome:
      | "NOT_PROVIDED"
      | "NOT_REQUIRED"
      | "UNDER_25000"
      | "FROM_25000_TO_49999"
      | "FROM_50000_TO_74999"
      | "FROM_75000_TO_99999"
      | "FROM_100000_TO_149999"
      | "FROM_150000_TO_199999"
      | "FROM_200000_TO_249999"
      | "FROM_250000_TO_299999"
      | "FROM_300000_TO_399999"
      | "FROM_400000_TO_499999"
      | "OVER_500000";
    mostRecentEviction:
      | "NOT_PROVIDED"
      | "NOT_REQUIRED"
      | "NEVER"
      | "LESS_THAN_3_YEARS_AGO"
      | "BETWEEN_3_AND_7_YEARS_AGO";
    mostRecentEvictionProvided: Date | null;
  } | null;
  profile?: {
    id: string;
    createdAt: Date;
    updatedAt: Date;
    createdById: string;
    updatedById: string | null;
    deletedAt: Date | null;
    deletedById: string | null;
    emails: any; // JSON field
    phoneNumbers: any; // JSON field
    addresses: any | null; // JSON field
  } | null;
  avatarFile?: {
    url: string;
    id: string;
    filename: string;
  } | null;
};

/**
 * User-like type for helper functions
 * Accepts any object that might have user fields
 */
export type UserLike = Partial<SessionUser>;

/**
 * Better Auth Session (uses base type from better-auth.ts)
 * Note: Admin plugin adds impersonatedBy field to session object at runtime
 * but it's not in the TypeScript types, so we use type assertions where needed
 */
export type Session = BetterAuthSession;

/**
 * Helper to check if user has role field (from database query)
 */
export function isExtendedUser(user: unknown): user is ExtendedUser {
  return (
    typeof user === "object" &&
    user !== null &&
    "id" in user &&
    ("role" in user ||
      "banned" in user ||
      "firstName" in user ||
      "lastName" in user)
  );
}

/**
 * Extracts the role field from a user object
 *
 * This function safely extracts the role value from any user-like object,
 * handling both session users and extended database users. The role field
 * is added by Better Auth's admin plugin and contains comma-separated role values.
 *
 * @param user - Any user-like object (session user, extended user, or partial)
 * @returns The user's role string (e.g., "admin", "user") or null if not available
 *
 * @example
 * ```ts
 * const role = extractUserRole(session?.user);
 * if (role?.includes("admin")) {
 *   // User has admin role
 * }
 * ```
 *
 * @remarks
 * - Renamed from `getUserRole` for clarity (ADR-008)
 * - Works with both session users and full database user objects
 * - Returns null for non-extended users (minimal session data)
 * - Role field format: comma-separated string (e.g., "admin,moderator")
 *
 * @see isExtendedUser
 * @see SessionUser
 * @see ExtendedUser
 */
// export function extractUserRole(
//   user: UserLike | null | undefined,
// ): string | null {
//   if (!user) return null;
//   if (isExtendedUser(user)) {
//     return user.role ?? null;
//   }
//   return null;
// }

export function isPlatformAdmin(user: UserLike | null | undefined): boolean {
  return user?.role?.split(",").includes("admin") ?? false;
}
